/**
 * Canonical URL helpers — sitewide route construction with slug validation.
 *
 * Why this exists
 * ===============
 * Several SEO/LLM content components rendered hidden/visible URLs like
 * `https://fridge.com/products/undefined` because of inline patterns such as:
 *
 *     `https://fridge.com${product.canonical_url || `/products/${product.seo_slug || product.normalized_model}`}`
 *
 * When the upstream `product` object carries the slug under a *camelCase*
 * field (`seoSlug`/`canonicalUrl`/`normalizedModel`) but the component
 * reads *snake_case* (`seo_slug`/`canonical_url`/`normalized_model`), all
 * three lookups are `undefined`, the template literal interpolates the
 * literal string `"undefined"`, and the SEO content emits a broken
 * `/products/undefined` URL on every page.
 *
 * That bug shipped to production on every product page (6 occurrences each)
 * and surfaced in `<meta itemProp="url">`, hidden citation prose, AND
 * sitewide internal anchor tags — all from one bad template literal.
 * Incident: 2026-05-08.
 *
 * The contract
 * ============
 *   • `getProductPath(input)`  → "/products/<slug>" or null
 *   • `getProductUrl(input)`   → "https://fridge.com/products/<slug>" or null
 *   • Same shape for collection / article / tool / brand / hub.
 *   • `isValidSlug(s)` is the single source of truth for what counts as a
 *     real, renderable slug — non-empty string, not literal "undefined" /
 *     "null" / "NaN" / unresolved template tokens.
 *
 * Renderers should call these helpers ONCE at the boundary and check for
 * `null` to decide whether to render the link, JSON-LD entry, or sitemap
 * row. Never inline `/products/${x.slug}` again.
 */

import { isPartType, familyForType, familyRoot, productPagePath } from '@/lib/parts/fpn'

export const SITE_ORIGIN = 'https://fridge.com'

/**
 * Tokens that look like strings but are actually JS coercion errors —
 * `${undefined}` becomes "undefined", `String(null)` is "null", broken
 * arithmetic gives "NaN". We've also seen unsubstituted Next.js route
 * tokens (`[slug]`), template tokens (`${slug}`), and SQL-injection-style
 * placeholders (`:slug`) leak through.
 */
const FORBIDDEN_SLUG_VALUES = new Set([
  'undefined',
  'null',
  'nan',
  'none',
  'false',
  'true',
])

const FORBIDDEN_SLUG_PATTERNS: RegExp[] = [
  /\$\{[^}]*\}/,    // `${slug}` unresolved template token
  /\[[^\]]+\]/,     // `[slug]` Next.js route token
  /^:[a-z]/i,       // `:slug` Express-style param
]

/**
 * Returns true if the input looks like a renderable slug — non-empty
 * string, not a literal coercion-error token, no unresolved templates.
 *
 * Whitespace is rejected (trailing/leading whitespace usually means the
 * caller built the slug from a dirty source).
 *
 * @example isValidSlug('avanti-refrigerator-ar17t1b-fin0000719') → true
 * @example isValidSlug('undefined') → false   // literal string
 * @example isValidSlug(undefined)   → false
 * @example isValidSlug('')          → false
 * @example isValidSlug('${slug}')   → false
 * @example isValidSlug('a/b')       → false   // contains path separator
 */
export function isValidSlug(value: unknown): value is string {
  if (typeof value !== 'string') return false
  if (value.length === 0) return false
  if (value !== value.trim()) return false
  if (FORBIDDEN_SLUG_VALUES.has(value.toLowerCase())) return false
  if (value.includes('/')) return false   // slug must not be a path
  if (value.includes('\\')) return false  // and not Windows path either
  for (const re of FORBIDDEN_SLUG_PATTERNS) {
    if (re.test(value)) return false
  }
  return true
}

/**
 * Picks the first valid slug from a list of candidates. Useful when the
 * upstream record stores the slug under multiple field names (snake +
 * camel) or has fallback identifiers (model number).
 */
export function pickFirstValidSlug(...candidates: Array<unknown>): string | null {
  for (const c of candidates) {
    if (isValidSlug(c)) return c
  }
  return null
}

/**
 * Validate an existing path, e.g. `canonical_url = '/products/foo'`.
 * Returns the path string if it parses cleanly; null if it's missing or
 * contains forbidden tokens.
 */
export function validatePath(value: unknown, expectedPrefix?: string): string | null {
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  if (trimmed.length === 0) return null
  if (!trimmed.startsWith('/')) return null
  if (trimmed.includes('//')) return null               // protocol slip / typo
  if (trimmed.includes('/undefined')) return null       // the bug class
  if (trimmed.includes('/null')) return null
  if (trimmed.includes('/NaN')) return null
  if (trimmed.includes('/nan')) return null
  for (const re of FORBIDDEN_SLUG_PATTERNS) {
    if (re.test(trimmed)) return null
  }
  if (expectedPrefix && !trimmed.startsWith(expectedPrefix)) return null
  return trimmed
}

/**
 * Build an absolute URL from a path. Returns null when the path is
 * invalid; otherwise prepends the canonical site origin.
 */
export function toAbsoluteUrl(path: string | null): string | null {
  if (!path) return null
  return `${SITE_ORIGIN}${path}`
}

// ─── Product ──────────────────────────────────────────────────────────────

interface ProductLikeForUrl {
  // Snake_case (DB row shape)
  canonical_url?: string | null
  seo_slug?: string | null
  normalized_model?: string | null
  // CamelCase (API response shape, after page.tsx getProductData() transform)
  canonicalUrl?: string | null
  seoSlug?: string | null
  normalizedModel?: string | null
  // Last-resort identifier
  model?: string | null
  /**
   * products.type — decides the route FAMILY (FRIDGE-333 · FRIDGE-396). A part
   * lives at /part/<slug>, a filter at /filter/<slug>; everything else at
   * /products/<slug>. Optional: a caller that
   * does not select `type` gets the /products/ form, which still resolves (the
   * edge 301s a part to its home) — it just costs the visitor a hop, so select
   * it wherever the row is a candidate part.
   */
  type?: string | null
}

/**
 * Build the product's page path from a product record. Accepts either
 * snake_case or camelCase field names — the same bug class returns when
 * callers forget that getProductData() renames fields.
 *
 * FRIDGE-333: the FAMILY comes from `type` and is decided FIRST, before the
 * stored `canonical_url` — for parts and filters that column can read
 * `/products/<slug>` (152 active rows on 10/20/2026, all written before
 * 05/07/2026; the insert trigger writes canonical_url for appliance types only,
 * FRIDGE-1677), so trusting it would emit the redirecting form on every surface
 * that links a filter or a gasket.
 *
 * Tries (in order): the family path from type+slug → canonical_url path →
 * seo_slug → normalized_model → model. Returns null if no valid identifier can
 * be extracted.
 */
export function getProductPath(p: ProductLikeForUrl | null | undefined): string | null {
  if (!p) return null
  // 0. A part's or filter's home is its own family's URL, whatever the stored
  //    column says (/part/ or /filter/ by type — FRIDGE-396).
  if (isPartType(p.type)) {
    const partSlug = pickFirstValidSlug(p.seo_slug, p.seoSlug, p.normalized_model, p.normalizedModel, p.model)
    if (partSlug) return `${familyRoot(familyForType(p.type))}/${partSlug}`
    // No usable slug: fall through and let the checks below return null.
  }
  // 1. Existing canonical path (already validated)
  const canonical =
    validatePath(p.canonical_url, '/products/') ??
    validatePath(p.canonicalUrl, '/products/')
  if (canonical) return canonical
  // 2. Build from a slug
  const slug = pickFirstValidSlug(
    p.seo_slug, p.seoSlug,
    p.normalized_model, p.normalizedModel,
    p.model,
  )
  if (!slug) return null
  return `/products/${slug}`
}

export function getProductUrl(p: ProductLikeForUrl | null | undefined): string | null {
  return toAbsoluteUrl(getProductPath(p))
}

// ─── Product cards ────────────────────────────────────────────────────────

/** The fields a product card, rail or list row may carry about its page. */
export interface ProductCardLike {
  type?: string | null
  seo_slug?: string | null
  seoSlug?: string | null
  canonical_url?: string | null
  canonicalUrl?: string | null
}

/** A stored item page: one slug under /products/, /parts/ or /filters/, as a path or a fridge.com URL. */
const STORED_ITEM_PAGE = /^(?:https?:\/\/(?:www\.)?fridge\.com)?\/(products|parts|filters)\/([^/?#]+)\/?(?:[?#].*)?$/i

/**
 * The product paths middleware.ts answers 410 as a malformed product id
 * (`/^\/products\/[a-f0-9]{5,}-/`): a row id (uuid) above all. A card never links one.
 */
const GONE_PRODUCT_ID_PATH = /^\/products\/[a-f0-9]{5,}-/

/**
 * FRIDGE-1705: the page a product card, rail or list links, built from what the
 * row is, never from its id. Part pages linked four active parts at
 * `/products/<uuid>`, which middleware answers 410, because the Top-Rated rail
 * fell back to the row id when canonical_url was empty.
 *
 *   1. the slug's page, rooted by type through the identifier module's route
 *      helper (lib/parts/fpn.ts productPagePath): `/parts/<slug>`,
 *      `/filters/<slug>` or `/products/<slug>`;
 *   2. else the stored canonical_url when it names an item page, as a site path
 *      or a fridge.com URL. The family root comes from the type (FRIDGE-333),
 *      so a part or filter stored under /products/ links its own root; a row
 *      read without its type keeps the stored root, which then also wins over
 *      the slug's untyped /products/ form;
 *   3. else null: the item has no page, and the caller renders no link — never
 *      a dead one, never `/products/<id>`.
 */
export function productCardPath(p: ProductCardLike | null | undefined): string | null {
  if (!p) return null
  const typed = typeof p.type === 'string' && p.type.trim().length > 0
  const stored = storedItemPagePath(p.canonical_url, p.type) ?? storedItemPagePath(p.canonicalUrl, p.type)
  const slug = pickFirstValidSlug(p.seo_slug, p.seoSlug)
  const path = slug && (typed || !stored) ? productPagePath({ type: p.type, seo_slug: slug }) : stored
  return path && !GONE_PRODUCT_ID_PATH.test(path) ? path : null
}

function storedItemPagePath(value: unknown, type: string | null | undefined): string | null {
  if (typeof value !== 'string') return null
  const m = value.trim().match(STORED_ITEM_PAGE)
  if (!m || !isValidSlug(m[2]) || !validatePath(`/${m[1]}/${m[2]}`)) return null
  const root = typeof type === 'string' && type.trim() ? familyRoot(familyForType(type)) : `/${m[1].toLowerCase()}`
  return `${root}/${m[2]}`
}

// ─── Collection ───────────────────────────────────────────────────────────

interface CollectionLikeForUrl {
  canonical_url?: string | null
  canonicalUrl?: string | null
  slug?: string | null
  collection_slug?: string | null
  collectionSlug?: string | null
}

export function getCollectionPath(c: CollectionLikeForUrl | null | undefined): string | null {
  if (!c) return null
  const canonical =
    validatePath(c.canonical_url, '/collections/') ??
    validatePath(c.canonicalUrl, '/collections/')
  if (canonical) return canonical
  const slug = pickFirstValidSlug(c.slug, c.collection_slug, c.collectionSlug)
  if (!slug) return null
  return `/collections/${slug}`
}

export function getCollectionUrl(c: CollectionLikeForUrl | null | undefined): string | null {
  return toAbsoluteUrl(getCollectionPath(c))
}

// ─── Article (blog) ───────────────────────────────────────────────────────

interface ArticleLikeForUrl {
  slug?: string | null
  article_slug?: string | null
  articleSlug?: string | null
  canonical_url?: string | null
  canonicalUrl?: string | null
}

export function getArticlePath(a: ArticleLikeForUrl | null | undefined): string | null {
  if (!a) return null
  const canonical =
    validatePath(a.canonical_url, '/blogs/news/') ??
    validatePath(a.canonicalUrl, '/blogs/news/')
  if (canonical) return canonical
  const slug = pickFirstValidSlug(a.slug, a.article_slug, a.articleSlug)
  if (!slug) return null
  return `/blogs/news/${slug}`
}

export function getArticleUrl(a: ArticleLikeForUrl | null | undefined): string | null {
  return toAbsoluteUrl(getArticlePath(a))
}

// ─── Tool ─────────────────────────────────────────────────────────────────

interface ToolLikeForUrl {
  slug?: string | null
  tool_slug?: string | null
  toolSlug?: string | null
}

export function getToolPath(t: ToolLikeForUrl | null | undefined): string | null {
  if (!t) return null
  const slug = pickFirstValidSlug(t.slug, t.tool_slug, t.toolSlug)
  if (!slug) return null
  return `/tools/${slug}`
}

export function getToolUrl(t: ToolLikeForUrl | null | undefined): string | null {
  return toAbsoluteUrl(getToolPath(t))
}

// ─── Brand ────────────────────────────────────────────────────────────────

interface BrandLikeForUrl {
  slug?: string | null
  brand_slug?: string | null
  brandSlug?: string | null
}

export function getBrandPath(b: BrandLikeForUrl | null | undefined): string | null {
  if (!b) return null
  const slug = pickFirstValidSlug(b.slug, b.brand_slug, b.brandSlug)
  if (!slug) return null
  return `/brands/${slug}`
}

export function getBrandUrl(b: BrandLikeForUrl | null | undefined): string | null {
  return toAbsoluteUrl(getBrandPath(b))
}

// ─── Hub ──────────────────────────────────────────────────────────────────

interface HubLikeForUrl {
  slug?: string | null
  hub_slug?: string | null
  hubSlug?: string | null
}

export function getHubPath(h: HubLikeForUrl | null | undefined): string | null {
  if (!h) return null
  const slug = pickFirstValidSlug(h.slug, h.hub_slug, h.hubSlug)
  if (!slug) return null
  return `/${slug}`
}

export function getHubUrl(h: HubLikeForUrl | null | undefined): string | null {
  return toAbsoluteUrl(getHubPath(h))
}

// ─── Sweep guard ──────────────────────────────────────────────────────────

/**
 * Final-line-of-defense check: scans an arbitrary URL/path for the
 * forbidden coercion-error tokens. Use when you can't migrate a caller to
 * the typed helpers above (e.g., URLs from third-party data, generated
 * content fields stored in the DB).
 *
 * @returns the URL unchanged if clean, null if it contains "/undefined",
 *          "/null", "/NaN", or unresolved template tokens.
 */
export function sanitizeRenderedUrl(url: string | null | undefined): string | null {
  if (typeof url !== 'string' || url.length === 0) return null
  // Quick rejects
  if (url.includes('/undefined')) return null
  if (url.includes('/null')) return null
  if (url.includes('/NaN')) return null
  if (url.includes('/nan')) return null
  for (const re of FORBIDDEN_SLUG_PATTERNS) {
    if (re.test(url)) return null
  }
  return url
}
